/**
 * FtpSelectionStep - Step 2 of match creation wizard
 *
 * Displays FTP targets grouped by default status:
 * - Default targets: Always included, shown with locked badge
 * - Available targets: Selectable via checkboxes
 */

import { useState, useEffect } from 'react';
import Spinner from '../../../../shared/components/ui/Spinner/Spinner';
import styles from './MatchModal.module.css';

/**
 * FTP target card component
 */
function FtpTargetCard({ target, isDefault, isSelected, onToggle }) {
  return (
    <label
      className={`${styles.ftpCard} ${isDefault ? styles.ftpCardDefault : ''} ${isSelected ? styles.ftpCardSelected : ''}`}
    >
      {!isDefault && (
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={isSelected}
          onChange={() => onToggle(target.id)}
        />
      )}
      <div className={styles.ftpCardContent}>
        <div className={styles.ftpCardHeader}>
          <span className={styles.ftpCardName}>{target.name}</span>
          {isDefault && (
            <span className={styles.defaultBadge}>Default</span>
          )}
        </div>
        <span className={styles.ftpCardHost}>{target.host}</span>
      </div>
    </label>
  );
}

/**
 * FtpSelectionStep component
 *
 * @param {Object} props
 * @param {string[]} props.selectedIds - Array of selected FTP target IDs
 * @param {(ids: string[]) => void} props.onChange - Callback when selection changes
 * @param {Function} props.fetchFtpTargets - API function to fetch FTP targets
 */
export default function FtpSelectionStep({ selectedIds, onChange, fetchFtpTargets }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [available, setAvailable] = useState([]);

  useEffect(() => {
    let mounted = true;

    async function loadTargets() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFtpTargets();
        if (mounted) {
          setDefaults(data.defaults || []);
          setAvailable(data.available || []);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message || 'Failed to load FTP targets');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadTargets();

    return () => {
      mounted = false;
    };
  }, [fetchFtpTargets]);

  const handleToggle = (targetId) => {
    const idStr = String(targetId);
    if (selectedIds.includes(idStr)) {
      onChange(selectedIds.filter((id) => id !== idStr));
    } else {
      onChange([...selectedIds, idStr]);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="md" />
        <span>Loading FTP destinations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    );
  }

  const totalCount = defaults.length + selectedIds.length;

  return (
    <div className={styles.ftpSelectionStep}>
      <p className={styles.stepDescription}>
        Select which FTP destinations should receive photos from this match.
        Default destinations are always included.
      </p>

      {/* Default Targets Section */}
      {defaults.length > 0 && (
        <div className={styles.ftpSection}>
          <h4 className={styles.ftpSectionTitle}>
            Default Destinations ({defaults.length})
          </h4>
          <p className={styles.ftpSectionHint}>
            These destinations receive photos from all matches in your company.
          </p>
          <div className={styles.ftpGrid}>
            {defaults.map((target) => (
              <FtpTargetCard
                key={target.id}
                target={target}
                isDefault={true}
                isSelected={false}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Targets Section */}
      {available.length > 0 && (
        <div className={styles.ftpSection}>
          <h4 className={styles.ftpSectionTitle}>
            Additional Destinations ({available.length})
          </h4>
          <p className={styles.ftpSectionHint}>
            Select additional destinations for this specific match.
          </p>
          <div className={styles.ftpGrid}>
            {available.map((target) => (
              <FtpTargetCard
                key={target.id}
                target={target}
                isDefault={false}
                isSelected={selectedIds.includes(String(target.id))}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      )}

      {/* No targets message */}
      {defaults.length === 0 && available.length === 0 && (
        <div className={styles.noTargets}>
          <p>No FTP destinations configured for your company.</p>
          <p className={styles.noTargetsHint}>
            You can add FTP destinations in Settings {'>'} FTP Targets.
          </p>
        </div>
      )}

      {/* Summary */}
      <div className={styles.ftpSummary}>
        <span className={styles.ftpSummaryCount}>
          {totalCount} destination{totalCount !== 1 ? 's' : ''} selected
        </span>
      </div>
    </div>
  );
}
